import {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Dialog, Flex, HStack, IconButton, Portal, Spinner, Text, useBreakpointValue, useMediaQuery} from '@chakra-ui/react';
import {Info, MessageSquare, X} from 'lucide-react';
import {DialogCloseButton} from '../../components/ui/dialog-close-button';
import {DriveFile} from '../../drive/types';
import {ExifSidebar} from '../../components/ExifSidebar';
import {CommentsPanel} from '../../components/comments/CommentsPanel';
import {FileVersionsPanel} from '../../components/FileVersionsPanel';
import {MoveFileTrailItem} from '../../components/MoveFileModal';
import {useContextMenu} from '../../components/FileContextMenu';
import {useFileComments, useFileRevisions} from '../../drive/queries';
import {getOpenThreads} from '../../domain/comments';
import {parseCommentAnchor} from '../../domain/commentAnchor';
import {displayedDimensions} from '../../domain/aspectRatio';
import {useCommentAnchoring} from '../../components/comments/useCommentAnchoring';
import {numberRevisions, selectedOlderRevision} from '../../domain/revisions';
import {PlazmaStars} from '../../hooks/usePlazmaStars';
import {copyFileName} from '../../components/copyFileName';
import {SwipeAction} from '../../domain/swipe';
import {useViewerImage} from './useViewerImage';
import {useViewerActions, ViewerAction} from './useViewerActions';
import {useViewerShortcuts} from './useViewerShortcuts';
import {COMMENTS_PANEL, INFO_PANEL, useViewerPanelState, ViewerPanel, ViewerSidebar} from './viewerPanels';
import {HistogramView} from './HistogramView';
import {histogramSource} from './useHistogram';
import {ShownImage, ViewerStage, ViewerStageHandle} from './ViewerStage';
import {ViewerTopBar} from './ViewerTopBar';
import { OlderVersionNotice } from '../../components/OlderVersionNotice';
import {slimScrollbar} from '../../components/ui/scrollbar';

/** Windows too short for a sidebar panel that scrolls its parts itself (phones in landscape are at most ~430 px). */
const SHORT_WINDOW_QUERY = '(max-height: 499px)';

export interface ViewerOverlayProps {
    files: DriveFile[];
    filesLoading: boolean;
    parentFolderId: string;
    sourceDriveId: string;
    initialTrail: MoveFileTrailItem[];
    showArchiveShortcut?: boolean;
    archiveFolderId?: string;
    /** Set in the web bin: shows the star (copy to plazma) button. */
    plazma?: PlazmaStars;
    currentFileId: string;
    onClose: () => void;
    onNavigateFile: (fileId: string) => void;
}

/**
 * The full-screen photo viewer: a top bar, the zoomable picture between optional side panels, and
 * the actions' dialogs. The pieces live next to it in this folder; this only composes them.
 */
export function ViewerOverlay(props: ViewerOverlayProps) {
    const {files, filesLoading, currentFileId, onClose} = props;
    const current = files.find((f) => f.id === currentFileId);

    useEffect(() => {
        // Hide scrollbar when the full screen viewer is open
        document.body.style.overflow = 'hidden';

        // Cleanup function to restore the scrollbar when the component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    });

    if (!current) {
        return <Dialog.Root
            open
            onOpenChange={(e) => {
                if (!e.open) onClose();
            }}
            size="full"
        >
            <Portal>
            <Dialog.Backdrop/>
            <Dialog.Positioner>
            <Dialog.Content bg="black" m={0} borderRadius={0}>
                <DialogCloseButton color="white" zIndex={2} _hover={{bg: 'whiteAlpha.200'}}/>
                <Dialog.Body display="flex" alignItems="center" justifyContent="center" h="100vh">
                    {filesLoading
                        ? <Spinner color="white" boxSize="12"/>
                        : <Text color="white">A kép nem található.</Text>
                    }
                </Dialog.Body>
            </Dialog.Content>
            </Dialog.Positioner>
            </Portal>
        </Dialog.Root>;
    }

    return <PhotoViewer {...props} current={current}/>;
}

function PhotoViewer({
    files,
    parentFolderId,
    sourceDriveId,
    initialTrail,
    showArchiveShortcut,
    archiveFolderId,
    plazma,
    current,
    onClose,
    onNavigateFile,
}: ViewerOverlayProps & { current: DriveFile }) {
    // Phones (below md) get one sheet of panels and a "⋯" menu; read at once, so it never flips after mounting.
    const isMobile = useBreakpointValue({base: true, md: false}, {ssr: false}) ?? false;
    // A phone in landscape is wide enough for the side panels, but too short for the comments' panel to
    // split its height with the versions: there the whole sidebar scrolls instead.
    const [shortWindow] = useMediaQuery([SHORT_WINDOW_QUERY], {ssr: false});
    const panelState = useViewerPanelState(isMobile);
    const contextMenu = useContextMenu();
    const stageRef = useRef<ViewerStageHandle>(null);
    // Which photo is zoomed in, so the next one (which the stage starts at fit) doesn't fetch its original.
    const [zoomedFileId, setZoomedFileId] = useState<string | null>(null);
    // The decoded image the stage shows, which the histogram reads instead of downloading its own.
    const [shownImage, setShownImage] = useState<ShownImage | null>(null);
    const currentId = current.id;
    const handleZoomedChange = useCallback(
        (zoomed: boolean) => setZoomedFileId(zoomed ? currentId : null),
        [currentId]
    );

    // An older version picked in the versions panel, for the photo it was picked for.
    const [revisionChoice, setRevisionChoice] = useState<{ fileId: string; revisionId: string }>();
    if (revisionChoice && revisionChoice.fileId !== currentId) setRevisionChoice(undefined);
    const selectedRevisionId = revisionChoice?.fileId === currentId ? revisionChoice.revisionId : undefined;
    // Shares the panel's cache entry; only needed (and fetched) once a version is picked.
    const revisionsQuery = useFileRevisions(selectedRevisionId ? currentId : undefined);
    const olderRevision = selectedOlderRevision(numberRevisions(revisionsQuery.data), selectedRevisionId);
    const handleSelectRevision = (revisionId: string | undefined) =>
        setRevisionChoice(revisionId ? {fileId: currentId, revisionId} : undefined);

    const index = files.indexOf(current);
    const prev = index > 0 ? files[index - 1] : undefined;
    const next = index < files.length - 1 ? files[index + 1] : undefined;

    const image = useViewerImage({
        files,
        index,
        // The info panel reads EXIF from the listing's imageMediaMetadata, or else from the original.
        needsMetadata: panelState.isShown(INFO_PANEL) && !current.imageMediaMetadata,
        zoomed: zoomedFileId === current.id,
        revision: olderRevision?.revision,
    });

    // Shared cache with the sidebar's comment list; drives the unresolved-count badge.
    const openThreads = getOpenThreads(useFileComments(current.id).data);
    const openCommentCount = openThreads.length;

    // Comments anchored to regions of the picture: drawn on the stage, listed in the comments panel.
    const anchoring = useCommentAnchoring(current.id);
    const shownImageOfCurrent = shownImage?.fileId === current.id ? shownImage : null;
    const commentedPicture = {
        url: shownImageOfCurrent?.url,
        size: displayedDimensions(current) ?? (shownImageOfCurrent?.element.naturalWidth
            ? {width: shownImageOfCurrent.element.naturalWidth, height: shownImageOfCurrent.element.naturalHeight}
            : null),
    };
    const shownThread = openThreads.find((c) => c.id === anchoring.shownCommentId);
    const stageAnchors = {
        drawing: anchoring.drawing,
        draft: anchoring.draft,
        shown: parseCommentAnchor(shownThread?.anchor)?.rect ?? null,
        onDrawn: anchoring.finishDrawing,
    };

    function goTo(file: DriveFile | undefined) {
        if (file) onNavigateFile(file.id);
        else onClose();
    }

    const viewerActions = useViewerActions({
        current,
        parentFolderId,
        sourceDriveId,
        initialTrail,
        archiveFolderId: showArchiveShortcut ? archiveFolderId : undefined,
        plazma,
        contextMenu,
        onRemoved: () => goTo(next ?? prev),
    });

    // In the phones' sheet order: the comments last, where their composer can stay at the bottom.
    const panels: ViewerPanel[] = [
        {
            ...INFO_PANEL,
            title: 'Információk',
            toggleLabel: 'Információk megjelenítése/elrejtése',
            toggleTitle: 'Hisztogram és fájladatok (I)',
            icon: <Info size={18}/>,
            keys: ['i'],
            content: <>
                <HistogramView
                    source={histogramSource(current, shownImage)}
                    unavailable={image.source === 'unavailable' || !!image.loadError}
                />
                <ExifSidebar file={current} blob={image.blob} blobError={image.blobError} originalUnreadable={!image.displayable}/>
            </>,
        },
        {
            ...COMMENTS_PANEL,
            title: 'Verziók és megjegyzések',
            toggleLabel: 'Verziók és megjegyzések megjelenítése/elrejtése',
            toggleTitle: openCommentCount > 0
                ? `Verziók és megjegyzések · ${openCommentCount} nyitott megjegyzés (C)`
                : 'Verziók és megjegyzések (C)',
            icon: <MessageSquare size={18}/>,
            badge: openCommentCount,
            keys: ['c'],
            // The versions take at most half of the height, the comments the rest; each scrolls on its own.
            fill: !shortWindow,
            content: <>
                <Box
                    borderBottomWidth="1px"
                    borderColor="whiteAlpha.200"
                    flexShrink={0}
                    maxH={shortWindow ? undefined : '50%'}
                    overflowY={shortWindow ? undefined : 'auto'}
                    css={slimScrollbar}
                >
                    <FileVersionsPanel
                        fileId={current.id}
                        selectedRevisionId={olderRevision?.revision.id}
                        onSelect={handleSelectRevision}
                    />
                </Box>
                <CommentsPanel
                    fileId={current.id}
                    anchoring={anchoring}
                    picture={commentedPicture}
                    shownRevisionId={olderRevision?.revision.id}
                    onViewRevision={handleSelectRevision}
                />
            </>,
        },
    ];
    // Keyboard only: the panels' toggles in the top bar show them already.
    const panelActions: ViewerAction[] = panels.map((panel) => ({
        id: `panel-${panel.id}`,
        label: panel.toggleLabel,
        keys: panel.keys,
        bar: 'never',
        run: () => isMobile ? panelState.toggleSheet() : panelState.toggle(panel),
    }));

    useViewerShortcuts({
        actions: [...viewerActions.actions, ...panelActions],
        suspended: viewerActions.isBusy,
        onPrevious: () => goTo(prev),
        onNext: () => goTo(next),
        onClose: () => anchoring.drawing ? anchoring.cancelDrawing() : onClose(),
        onZoom: (factor) => stageRef.current?.zoomBy(factor),
        onResetZoom: () => stageRef.current?.resetZoom(),
    });

    const shownOn = (side: 'left' | 'right') => panels.filter((p) => p.side === side && panelState.isShown(p));

    const swipes: Partial<Record<SwipeAction, () => void>> = {
        previous: prev && (() => goTo(prev)),
        next: next && (() => goTo(next)),
        up: viewerActions.canArchive ? viewerActions.requestMoveToArchive : undefined,
        down: onClose,
    };

    const handleCopyName = () => copyFileName(current.name);

    return <Dialog.Root
        open
        onOpenChange={(e) => {
            if (!e.open) onClose();
        }}
        // Escape stops drawing a region before it closes the viewer.
        onEscapeKeyDown={(e) => {
            if (!anchoring.drawing) return;
            e.preventDefault();
            anchoring.cancelDrawing();
        }}
        size="full"
    >
        <Portal>
        <Dialog.Positioner>
        <Dialog.Content bg="black" m={0} borderRadius={0} h="100dvh" display="flex" flexDirection="column">
            <ViewerTopBar
                index={index}
                total={files.length}
                fileName={current.name}
                onCopyName={handleCopyName}
                actions={viewerActions.actions}
                originalSwitch={image.originalSwitch}
                panels={panels}
                panelState={panelState}
                isMobile={isMobile}
                onOpenMenu={contextMenu.openBelow}
                onClose={onClose}
            />

            <Dialog.Body p={0} flex={1} minH={0} display="flex" flexDirection={isMobile ? 'column' : 'row'} overflow="hidden">
                {!isMobile && <ViewerSidebar placement="left" panels={shownOn('left')} open={shownOn('left').length > 0}/>}
                {/* Not keyed by the photo: it keeps the previous picture up until the next one is decoded. */}
                <ViewerStage
                    ref={stageRef}
                    file={current}
                    image={image}
                    onPrevious={prev && (() => goTo(prev))}
                    onNext={next && (() => goTo(next))}
                    swipes={swipes}
                    onZoomedChange={handleZoomedChange}
                    onContextMenu={contextMenu.open}
                    onShownImage={setShownImage}
                    anchors={stageAnchors}
                >
                    {(anchoring.drawing || olderRevision) &&
                        <Flex position="absolute" top={3} left={0} right={0} zIndex={2} px={4} pointerEvents="none"
                                justify="center" align="center" gap={2} direction="column">
                            {olderRevision &&
                                <OlderVersionNotice revision={olderRevision} onBack={() => handleSelectRevision(undefined)} compact={isMobile}/>
                            }
                            {anchoring.drawing &&
                            <HStack
                                gap={1}
                                pl={3}
                                pr={1}
                                py={1}
                                bg="blackAlpha.800"
                                color="white"
                                fontSize="sm"
                                borderRadius="full"
                                pointerEvents="auto"
                                onPointerDown={(e) => e.stopPropagation()}
                                boxShadow="0 0 4px rgba(255, 255, 255, 0.5)"
                            >
                                <Text>Húzással jelölj ki egy területet a képen</Text>
                                <IconButton
                                    aria-label="Kijelölés megszakítása"
                                    title="Mégse (Esc)"
                                    size="xs"
                                    variant="ghost"
                                    color="white"
                                    borderRadius="full"
                                    _hover={{bg: 'whiteAlpha.200'}}
                                    onClick={anchoring.cancelDrawing}
                                >
                                    <X size={14}/>
                                </IconButton>
                            </HStack>
                            }
                        </Flex>
                    }
                    {isMobile &&
                        <Flex position="absolute" bottom={4} left={0} right={0} justify="center" px={4} pointerEvents="none">
                            <Text
                                color="white"
                                fontSize="sm"
                                bg="blackAlpha.600"
                                px={3}
                                py={1}
                                borderRadius="md"
                                cursor="pointer"
                                truncate
                                pointerEvents="auto"
                                onClick={handleCopyName}
                                // A tap here is not one of the picture's gestures.
                                onPointerDown={(e) => e.stopPropagation()}
                                title="Kattints a fájlnév másolásához"
                            >
                                {current.name}
                            </Text>
                        </Flex>
                    }
                </ViewerStage>
                {isMobile
                    ? <ViewerSidebar placement="sheet" panels={panels} open={panelState.sheetOpen}/>
                    : <ViewerSidebar placement="right" panels={shownOn('right')} open={shownOn('right').length > 0}/>
                }
            </Dialog.Body>

            {/* Inside the dialog content, so it's within the dialog's focus trap and stacking context. */}
            {viewerActions.contextMenuElement}
        </Dialog.Content>
        </Dialog.Positioner>
        </Portal>

        {viewerActions.dialogs}
    </Dialog.Root>;
}